<script setup lang="ts">
// Vue
import { computed, onUnmounted, ref, watch } from "vue";
import { Boxes, Plus, Waypoints } from "@lucide/vue";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";

// Types
import { ModelType, useEditBuildingProjectWizard } from "@/features/BuildingProject";

// Utils
import { getSpeckleStreamModels, useProjectSpeckleModelSync, type SpeckleStreamModel } from "@features/Speckle";
import { useModalStore } from "@/stores/modals/modals.store";
import { pendingRemovals } from "../../../services/pendingModelRemovals";

// Components
import Divider from "@/components/Reusables/Divider/Divider.vue";
import StepCard from "../StepCard.vue";
import StepIntro from "../StepIntro/StepIntro.vue";
import CustomModels from "./CustomModels.vue";
import NonSelectedModels from "./NonSelectedModels.vue";
import PendingRemovalModels from "./PendingRemovalModels.vue";
import SelectedModels from "./SelectedModels.vue";

/**
 * The project's named models, split out of the 3D model / Speckle connection screen (RTLCA-5661
 * follow-up); rows the frontend owns since RTLCA-5629, and since RTLCA-5745 rows the page's Save
 * writes, like every other field on it. Naming a model, pulling one in from the stream and turning one
 * on or off all land on the wrapper's own list and go to Hasura together when the reader saves — one
 * document that deletes and re-inserts the whole set with its flags, which the user role's permissions
 * are shaped for (no UPDATE by design: it would not fire the sync triggers). Naming one used to write
 * at once, which made it the only control here that Discard could not undo and Save had nothing to do
 * with. Until that Save lands the card marks the new rows "Will be added when you save", and the
 * Integrations section's Sync now — where the sync status and that button live since RTLCA-5745,
 * because syncing is the integration's job — is blocked: a pass makes Speckle match the SAVED rows, so
 * running one on an edit still in hand syncs a state this card no longer shows and then reports the two
 * as agreeing.
 *
 * Since RTLCA-5756 removing a model is held until Save too, and it was the last thing here that was
 * not. It wrote at once because the empty Speckle model goes with the row and only the reader's own
 * token may ask for that — a real constraint, but it does not need the ROW deleted at the click: the
 * save's one document re-inserts the whole set, so leaving a model out of it IS the delete. The row
 * therefore stays on the list wearing `pendingRemoval`, which `modelSignature` reads by leaving it out
 * (the rail lights, the write omits it, the trail says `Model "X" removed`), Undo takes back, and
 * Discard drops with every other edit. `runSave` deletes the models on the stream just before it
 * writes, so a model that turns out to hold a version is refused and KEPT rather than dropped.
 *
 * Deleting by row id is also how the old immediate write broke. The ids this page holds come from the
 * project read, and until RTLCA-5756 that read could hand over a Speckle model id where a
 * `project_model.id` belongs — so the delete matched no row, and its zero affected rows were reported
 * to the reader as a permissions problem. Nothing here names a row id any more.
 *
 * Once a sync pass has run — the nudge answered, or the queue row went — what the pass wrote is folded
 * in (`refreshModelSyncState`), so an edit not yet saved survives it and the cards stop saying
 * "Waiting for Speckle" without a reload.
 */
const editBuildingProjectWizard = useEditBuildingProjectWizard();
const toast = useToast();

const models = computed(() => editBuildingProjectWizard.getProjectWrapper().models);
const projectId = computed(() => editBuildingProjectWizard.getProjectWrapper().project.id);
const streamId = computed(() => editBuildingProjectWizard.getProjectWrapper().project.streamId || "");
const hasStream = computed(() => streamId.value.length > 0);

// What the save will leave behind, and what it will take. The two lists show the state the reader is
// heading for, so a model on its way out is not counted among the selected ones.
const staying = computed(() => models.value.filter((model) => !model.pendingRemoval));
const leaving = computed(() => pendingRemovals(models.value));

const getActiveModels = () => staying.value.filter((model) => model.isActive);
const getInactiveModels = () => staying.value.filter((model) => !model.isActive);

// Every name the project knows, including the ones on their way out: a staged removal must not turn up
// as an "on the stream" chip offering to add back a model already on the list, and re-typing its name
// must not get past the duplicate guards either. Undo is the way back.
const modelNames = computed(() => models.value.map((model) => model.name));

/**
 * The stream's models as the Speckle server has them, so a model created from the connector or on
 * Speckle can be pulled in here. `null` while unknown, in which case the row stays hidden. Re-read
 * when the stream changes (a connect made on the 3D model screen) and after every change here.
 *
 * The whole models rather than their names (RTLCA-5756): the version count is what says whether a
 * model can still be removed, and having it here is what lets the click refuse on the spot instead of
 * staging something the save will only turn down.
 */
const streamModels = ref<SpeckleStreamModel[] | null>(null);
const loadStreamModels = async () => {
  streamModels.value = streamId.value ? await getSpeckleStreamModels(streamId.value) : null;
};
const stopStreamModelsWatch = watch(streamId, loadStreamModels, { immediate: true });
onUnmounted(() => stopStreamModelsWatch());

const streamModelNames = computed(() => streamModels.value?.map((model) => model.name) ?? null);

/**
 * Re-read the stream once a save finishes, because a save is now what changes it: the models staged
 * for removal are deleted from Speckle on the way to the row write. Without this the cached list would
 * still name them while the rows are already gone, and each would flash back up as a chip under "On
 * the stream, not in this project" offering to add it again. A re-read rather than dropping the names
 * locally, because it is also right about the ones the save REFUSED to remove — those are still on the
 * stream, and still in the project.
 *
 * The sync watcher below re-reads too, and gets there second: it waits for a whole pass to land.
 */
const stopSaveWatch = watch(
  () => editBuildingProjectWizard.getIsSaving(),
  (saving, wasSaving) => {
    if (!saving && wasSaving) void loadStreamModels();
  }
);
onUnmounted(() => stopSaveWatch());

const streamOnlyModelNames = computed(() => {
  if (!streamModelNames.value) return [];
  const known = new Set(modelNames.value.map((name) => name.toLowerCase()));
  return streamModelNames.value.filter((name) => !known.has(name.toLowerCase()));
});

// Typing a name that already exists on the stream must not try to create it a second time.
const existingNamesForCreate = computed(() => [...modelNames.value, ...streamOnlyModelNames.value]);

const sync = useProjectSpeckleModelSync(projectId, hasStream);

/**
 * Pull in what a sync pass wrote — the Speckle model id and version per row, and any model it
 * imported — so the cards stop saying "Waiting for Speckle" without a reload. Coalesced: the pass that
 * answered and the queue row it clears can both ask within the same second. Best-effort: the cards
 * only stay as they were, and the next pass asks again.
 */
let refreshInFlight: Promise<void> | null = null;
const refreshAfterSync = (): Promise<void> => {
  refreshInFlight ??= editBuildingProjectWizard
    .refreshModelSyncState(projectId.value)
    .catch((error: unknown) => {
      Sentry.captureException(error);
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

// The sweeper finished a pass (the row is gone), so its writes are what the cards should show now. The
// stream's own list is re-read with it: a save that removed a model deleted it from the stream too, and
// until this lands the cached list would still offer it back under "On the stream, not in this project".
const stopSyncWatch = watch(sync.status, (status, previous) => {
  if (status === "inSync" && (previous === "pending" || previous === "syncing" || previous === "failed")) {
    void refreshAfterSync();
    void loadStreamModels();
  }
});
onUnmounted(() => stopSyncWatch());

// Flips isActive on the wrapper's own model object, which is the edit the Save bar picks up.
const toggleModel = (model: ModelType) => {
  model.isActive = !model.isActive;
};

/**
 * Name a model. Nothing is written here: the row is pushed onto the wrapper's own list, where a toggle
 * lands, and the page's Save writes the whole list in one document — which is also what creates it on
 * Speckle. No id and no Speckle columns, because only a write mints those; the missing id is what the
 * card reads as an add not saved yet, what makes removing it local, and what the save's own diff sees
 * as a name the saved list does not have.
 *
 * Lower-cased, as the create wizard does: Speckle stores model names in lower case, and a row spelt
 * otherwise did not match the name Speckle's webhook sends on a publish.
 */
const addModelNamed = (typedName: string) => {
  const name = typedName.toLowerCase();
  // Both callers block a duplicate before offering it; this is what a double click cannot get past,
  // and what the (project, name) unique constraint would otherwise meet at Save.
  if (models.value.some((model) => model.name.toLowerCase() === name.toLowerCase())) return;
  models.value.push({ name, isActive: true });
};

const handleAddNewModel = (model: ModelType) => addModelNamed(model.name);

// It exists on Speckle already: the row is enough, the sync links the two by name at Save.
const includeStreamModel = (name: string) => addModelNamed(name);

const modals = useModalStore();

/**
 * Stage a SAVED model for removal: the way out of a mistyped name. Nothing is written and nothing is
 * deleted from the stream — the page's Save does both, in that order, so a refusal there can still keep
 * the model. The flag goes on the wrapper's own row, which is what Discard restores over and what
 * `modelSignature` leaves out of the set the save re-inserts.
 *
 * The version count is checked here as well as at the save, against the stream list this card already
 * holds, so a model that plainly cannot go is refused at the click rather than sitting in the removal
 * group until the reader saves and finds out. Not a substitute for the check at the save: this list can
 * be minutes old, and `deleteEmptySpeckleModel` re-reads the count immediately before deleting. When
 * the stream's answer is unknown (no linked Speckle account, server unreachable) this stages and the
 * save is the only gate — the same rule as before, where the refusal came from the same place.
 */
const stageRemoval = (model: ModelType) => {
  model.pendingRemoval = true;
};

const canRemoveNow = (model: ModelType) => {
  const onStream = streamModels.value?.find((candidate) => candidate.name.toLowerCase() === model.name.toLowerCase());
  if (!onStream || onStream.versionCount === 0) return true;
  toast.error(`"${model.name}" has published versions, so it was not removed.`);
  return false;
};

const undoRemoval = (model: ModelType) => {
  model.pendingRemoval = false;
};

/**
 * Remove a model the Save has never written: it exists on this list and nowhere else, so it goes from
 * this list and nothing else. No Speckle call — a Speckle model is created for a saved row, so there
 * is nothing there yet — no row delete, and no confirmation: undoing something typed a moment ago is
 * not a decision worth a dialog, and Discard drops it anyway. The stream list is left alone on
 * purpose: a model pulled in from the chips is still on the stream, so its chip must come back.
 */
const forgetLocalModel = (model: ModelType) => {
  const at = models.value.findIndex((candidate) => candidate.name.toLowerCase() === model.name.toLowerCase());
  if (at >= 0) models.value.splice(at, 1);
};

// The dialog still asks, though the click no longer destroys anything: a saved model is shared with
// everyone on the project and the stream, and what the save will do to it is worth stating once. It
// closes as soon as it is confirmed — staging is immediate, so there is nothing to wait for.
const handleRemoveModel = (model: ModelType) => {
  if (!model.id) return forgetLocalModel(model);
  if (!canRemoveNow(model)) return;

  return modals.open(
    "DeleteModal",
    {
      itemName: model.name,
      heading: "Remove model?",
      confirmLabel: "Remove",
      message: hasStream.value
        ? `"${model.name}" will be removed from this project when you save, and its empty model deleted from the stream with it. Nothing has been published to it, so nothing is lost. Its connector target goes with it.`
        : `"${model.name}" will be removed from this project when you save.`,
      onConfirm: () => stageRemoval(model),
    },
    { ariaLabel: `Remove model ${model.name}` }
  );
};

/**
 * What the save will do to this list, said once at the top of the card. The chips each carry their own
 * mark; this is the count, so the reader does not have to find it by reading three groups.
 *
 * Counted off the list itself — a row with no id has never been written, a flagged one is on its way
 * out — rather than diffed against the saved baseline, so the sentence and the chips cannot disagree.
 * Flips are left out on purpose: the chip's own tint already shows which side of the toggle it is on,
 * and the rail says the section is edited either way.
 */
const pendingSummary = computed(() => {
  const added = staying.value.filter((model) => !model.id).length;
  const removed = leaving.value.length;
  if (added === 0 && removed === 0) return "";

  // The noun agrees with the FIRST count when both clauses are present, which is the whole reason this
  // is not two sentences pasted together.
  const models_ = (count: number) => (count === 1 ? "model" : "models");
  if (added > 0 && removed > 0) return `${added} ${models_(added)} will be added and ${removed} removed when you save.`;
  if (added > 0) return `${added} ${models_(added)} will be added when you save.`;
  return `${removed} ${models_(removed)} will be removed when you save.`;
});
</script>

<template>
  <div class="flex w-full flex-col">
    <StepIntro
      class="mb-6"
      heading="Which models does this project include?"
      hint="A model holds one trade or discipline's geometry, published through a 3D model connection. Naming one here does not require a connection yet." />

    <StepCard
      :icon="Boxes"
      heading="Model configuration"
      :caption="
        hasStream
          ? 'Turn on the models to include in this project.'
          : 'Add the models this project will hold and turn on the ones to include. Connect a 3D model to publish geometry into them.'
      ">
      <!-- Kept in the DOM whether or not it says anything, so the live region announces a change
           rather than the region itself arriving. -->
      <div aria-live="polite">
        <p
          v-if="pendingSummary"
          class="t-caption text-app-yellow-700 flex items-center gap-1.5">
          <span
            class="bg-app-yellow-500 h-1.5 w-1.5 shrink-0 rounded-full"
            aria-hidden="true" />
          {{ pendingSummary }}
        </p>
      </div>

      <SelectedModels
        :models="getActiveModels()"
        @toggle-model="toggleModel"
        @remove-model="handleRemoveModel" />
      <NonSelectedModels
        :models="getInactiveModels()"
        @toggle-model="toggleModel"
        @remove-model="handleRemoveModel" />
      <PendingRemovalModels
        :models="leaving"
        @keep-model="undoRemoval" />

      <!-- Models the Speckle server holds for this stream that the project does not list yet:
           created from the connector or on Speckle. Hidden while the server's answer is unknown. -->
      <div
        v-if="streamOnlyModelNames.length > 0"
        class="flex flex-col gap-2">
        <span class="t-caption text-type-muted">On the stream, not in this project</span>
        <ul class="flex flex-wrap gap-2">
          <li
            v-for="name in streamOnlyModelNames"
            :key="name">
            <button
              type="button"
              class="focus-ring interactive-active rounded-8 bg-slate-0 t-small text-type-default duration-fast ease-standard hover:border-app-blue-400 hover:bg-app-blue-50 flex items-center gap-1.5 border border-dashed border-slate-300 px-3 py-2 transition-colors"
              :aria-label="`Include ${name} in this project`"
              @click="includeStreamModel(name)">
              <Plus
                class="icon-xs text-app-blue-600 shrink-0"
                :stroke-width="2"
                aria-hidden="true" />
              {{ name }}
            </button>
          </li>
        </ul>
        <p class="t-caption text-type-muted">Add one to include it in this project. It is saved with the project.</p>
      </div>

      <Divider spacing="sm" />
      <CustomModels
        :stream-id="streamId"
        :existing-model-names="existingNamesForCreate"
        @add-new-model="handleAddNewModel" />

      <RouterLink
        v-if="!hasStream"
        :to="{ name: 'projectSettingsSpeckle', params: { id: projectId } }"
        class="focus-ring rounded-8 t-small-bold text-type-default duration-fast ease-standard ring-slate-250 bg-slate-0 flex w-fit items-center gap-2 px-4 py-2 ring-1 transition-all hover:ring-slate-300">
        <Waypoints
          class="icon-sm text-app-blue-600"
          :stroke-width="1.5"
          aria-hidden="true" />
        Connect a 3D model
      </RouterLink>
    </StepCard>
  </div>
</template>
